import {
  HStack,
  Table,
  Tr,
  Tbody,
  Text,
  Button,
  Box,
  Spacer,
  useColorModeValue,
  Stack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogOverlay,
  TableContainer,
  Td,
  Th,
  Thead,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react';
import { Formik, FormikProps, getIn } from 'formik';
import * as _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';

import {
  checkFormula,
  prettyPrintFormula,
} from '@finley-technologies/calc-agent';
import { finleyAssert } from '@finley-technologies/common';
import {
  CreditAgreement,
  FormulaEngine,
} from '@finley-technologies/finley-types';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import { getIterationKey } from '../common';
import { FormulaEditor } from '../edit/MonacoEditor/ui/FormulaEditor';
import { colors } from 'packages/common-frontend/colors';
import { useDraggableRow } from 'packages/common-frontend/components/DragAndDropTable/hooks';
import { TrDraggableHandle } from 'packages/common-frontend/components/DragAndDropTable/ui';
import { TextInputFormField } from 'packages/common-frontend/components/FormField';
import { FormulaContainer } from 'packages/common-frontend/components/FormulaContainer';
import {
  EditIconButton,
  DeleteIconButton,
  XIconButton,
  PlusIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  formatSelectOptions,
  Select,
} from 'packages/common-frontend/components/Select';

type AddButtonProps = { onClick: () => void; title: string };
const AddButton = ({ onClick, title }: AddButtonProps) => {
  return (
    <Button
      leftIcon={<Icon icon="plus" />}
      colorScheme="v2-aurora"
      variant="link"
      py="10px"
      px="20px"
      onClick={onClick}
      size="sm"
    >
      {title}
    </Button>
  );
};

type DeleteModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSave: () => void;
  title: string;
  message: string;
};
const DeleteModal = ({
  isOpen,
  onCancel,
  onSave,
  title,
  message,
}: DeleteModalProps) => {
  const cancelRef = React.useRef(null);
  const buttonColorScheme = useColorModeValue('aurora', 'gray');
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCancel}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {title}
          </AlertDialogHeader>

          <AlertDialogBody>
            <>
              <Text mb={2}>{message}</Text>
              <Box py="12px" alignSelf="flex-end">
                <HStack spacing="25px">
                  <Spacer />
                  <Button
                    variant="outline"
                    colorScheme={buttonColorScheme}
                    onClick={() => onCancel()}
                    px="20px"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="solid"
                    colorScheme={buttonColorScheme}
                    onClick={() => {
                      onSave();
                    }}
                    px="20px"
                  >
                    Delete
                  </Button>
                </HStack>
              </Box>
            </>
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

/**
 * ============= MODIFIER NODE CONFIGURATION =============
 */

type ModifierNodeParams =
  | {
      depth: number;
      idx: number;
      isRoot?: never;
    }
  | {
      depth?: never;
      idx: number;
      isRoot: boolean;
    };

export const createModifierShell = (
  params: ModifierNodeParams,
): CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateModifierNode => ({
  name: params.isRoot
    ? `[ modifierTree.${params.idx} ]`
    : `[ modifierName:${params.depth}.${params.idx} ]`,
  isMatch: {
    formula: 'TRUE',
    engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
  },
  modifierFormula: {
    formula: 'advanceRate',
    engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
  },
  children: [],
});

type EditModifierNodeProps = {
  onSave: Function;
  onCancel: Function;
  node: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateModifierNode;
  operationMode?: CreditAgreement.OperationMode;
  baseFields: CreditAgreement.DataTapeField.BaseConfig[];
  intermediateCalcs: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[];
  concentrationLimitConfigs: CreditAgreement.Config.ConcentrationLimit.SourceConfig[];
};
const EditModifierNode = ({
  onSave,
  onCancel,
  node,
  operationMode,
  baseFields,
  intermediateCalcs,
  concentrationLimitConfigs,
}: EditModifierNodeProps) => {
  const [conditionErrors, setConditionErrors] = useState<string[]>([]);
  const [modifierError, setModifierError] = useState<string[]>([]);

  const isFormulaValid = useCallback(
    ({
      conditionToValidate,
      modifierToValidate,
    }: {
      conditionToValidate: string;
      modifierToValidate: string;
    }) => {
      const conditionFormulaErrors = checkFormula({
        formula: conditionToValidate,
        baseFields,
        intermediateCalcs,
        concentrationLimitConfigs,
      });

      const modifierFormulaErrors = checkFormula({
        formula: modifierToValidate,
        baseFields,
        intermediateCalcs,
        includeAdvanceRateVariable: true,
        concentrationLimitConfigs,
      });

      if (!modifierToValidate.includes('advanceRate')) {
        modifierFormulaErrors.push(
          'Variable: `advanceRate` must be included in the formula.',
        );
      }

      setConditionErrors(conditionFormulaErrors);
      setModifierError(modifierFormulaErrors);

      return (
        conditionFormulaErrors.length === 0 &&
        modifierFormulaErrors.length === 0
      );
    },
    [baseFields, intermediateCalcs, concentrationLimitConfigs],
  );
  return (
    <>
      <Formik
        initialValues={{ ...node }}
        onSubmit={(props) => {
          if (
            isFormulaValid({
              conditionToValidate: props.isMatch.formula,
              modifierToValidate: props.modifierFormula.formula,
            })
          ) {
            onSave({ ...props, name: _.camelCase(props.name) });
          }
        }}
      >
        {(
          localFormProps: FormikProps<CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateModifierNode>,
        ) => (
          <Stack spacing="20px">
            <TextInputFormField label="Modifier Name" name="name" />

            <HStack width="full" spacing="20px">
              <Box width="60%">
                <Text mb={2} fontSize="15px" fontWeight="bold">
                  Condition
                </Text>
                <Box
                  border="1px"
                  borderColor={colors.gray[200]}
                  borderRadius="8px"
                  p="8px"
                >
                  <FormulaEditor
                    value={localFormProps.values.isMatch.formula}
                    onChange={(value) => {
                      localFormProps.setFieldValue('isMatch.formula', value);
                    }}
                    operationMode={operationMode}
                    language="formula-receivableAdvanceRate"
                  />
                </Box>
              </Box>
              <Box width="30%">
                <Text mb={2} fontSize="15px" fontWeight="bold">
                  Formula
                </Text>
                <Box
                  border="1px"
                  borderColor={colors.gray[200]}
                  borderRadius="8px"
                  p="8px"
                >
                  <FormulaEditor
                    value={localFormProps.values.modifierFormula.formula}
                    onChange={(value) => {
                      localFormProps.setFieldValue(
                        'modifierFormula.formula',
                        value,
                      );
                    }}
                    operationMode={operationMode}
                    language="formula-receivableAdvanceRate"
                  />
                </Box>
              </Box>

              <Box width="10%">
                <Button
                  display="block"
                  w="100%"
                  mt="28px"
                  mb="16px"
                  color="white"
                  backgroundColor={colors.aurora[400]}
                  type="submit"
                  onClick={() => localFormProps.handleSubmit()}
                >
                  Save
                </Button>
                <Button
                  display="block"
                  w="100%"
                  color={colors.aurora[400]}
                  border="2px"
                  borderRadius="8px"
                  borderColor={colors.aurora[400]}
                  backgroundColor="white"
                  onClick={() => onCancel()}
                >
                  Cancel
                </Button>
              </Box>
            </HStack>
            {conditionErrors.length > 0 && (
              <Box
                width="full"
                padding="8px"
                borderRadius="8px"
                bgColor="red.100"
              >
                {`Condition Errors: ${JSON.stringify(conditionErrors)}`}
              </Box>
            )}
            {modifierError.length > 0 && (
              <Box
                width="full"
                padding="8px"
                borderRadius="8px"
                bgColor="red.100"
              >
                {`Modifier Formula Errors: ${JSON.stringify(modifierError)}`}
              </Box>
            )}
          </Stack>
        )}
      </Formik>
    </>
  );
};

type ModifierLevelTableProps = {
  modifierNode: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateModifierNode;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  fullPropertyKey: string;
  depth: number;
  isEditableIndex: boolean;
  childrenAtCurrentIndex: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateModifierNode[];
  directoriesBefore: string;
  directoryName: string;
  directoryIndex: number;
  propertyKeyBeforeRoot: string;
  propertyKeyFromRoot: string[];
  setSelectedModifierNode: React.Dispatch<
    React.SetStateAction<{ propertyKey: string; depth: number } | undefined>
  >;
};
const ModifierLevelTable = ({
  modifierNode,
  wipConfig,
  fullPropertyKey,
  depth,
  isEditableIndex,
  childrenAtCurrentIndex,
  directoriesBefore,
  directoryName,
  directoryIndex,
  propertyKeyBeforeRoot,
  propertyKeyFromRoot,
  setSelectedModifierNode,
}: ModifierLevelTableProps) => {
  const childrenPropertyKey = `${fullPropertyKey}.children`;

  const {
    useRowState,
    useOrderLockedState,
    moveRow,
    ui: { LockOrderButton, RestoreOrderButton, SaveOrderCallout },
  } = useDraggableRow<CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateModifierNode>(
    { comparisonKey: 'name' },
  );
  const [isOrderLocked] = useOrderLockedState();

  const [childrenNodeState, setChildrenNodeState] = useRowState(
    modifierNode.children,
  );

  const onSaveOrder = useCallback((): void => {
    wipConfig?.formProps.setFieldValue(
      `${childrenPropertyKey}`,
      childrenNodeState,
    );
  }, [childrenPropertyKey, childrenNodeState, wipConfig]);

  const addModifierNode = (
    wc: CreditAgreementFullArtifactWipConfigType,
  ): void => {
    const children = _.cloneDeep(
      getIn(wc.formProps.values, childrenPropertyKey),
    );

    const field = createModifierShell({
      depth,
      idx: children.length,
    });
    children.push(field);

    wc.formProps.setFieldValue(childrenPropertyKey, children);
    setChildrenNodeState(children);
  };

  const [nodeIdxToDelete, setNodeIdxToDelete] = useState<number>();

  const deleteModifierNode = (
    wc: CreditAgreementFullArtifactWipConfigType,
    idxToDelete: number,
  ): void => {
    const childrenCopy = _.cloneDeep(
      getIn(wc.formProps.values, childrenPropertyKey),
    );

    childrenCopy.splice(idxToDelete, 1);
    wc.formProps.setFieldValue(childrenPropertyKey, childrenCopy);
    setChildrenNodeState(childrenCopy);
  };

  return (
    <>
      {wipConfig?.isMutateAllowed(fullPropertyKey) && <SaveOrderCallout />}

      <HStack mb={2}>
        {isEditableIndex && wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <LockOrderButton onSaveOrder={onSaveOrder} />
            {!isOrderLocked && <RestoreOrderButton />}
          </>
        )}
        <Spacer />
        {isEditableIndex &&
          wipConfig?.isMutateAllowed(fullPropertyKey) &&
          isOrderLocked && (
            <AddButton
              title="Add Modifier Child"
              onClick={() => {
                addModifierNode(wipConfig);
              }}
            />
          )}
      </HStack>

      <Table
        style={{
          borderCollapse: 'separate',
          borderSpacing: '0 16px',
        }}
      >
        <Tbody>
          {!isEditableIndex &&
            childrenAtCurrentIndex.map((node, idx) => {
              const id = getIterationKey(node);

              const indexInDirectoryName = directoryName.match(
                /children\[(\d+)\]/,
              );
              finleyAssert(
                indexInDirectoryName && indexInDirectoryName.length === 2,
                'Every level but the current modifiers[0].children should follow this pattern: "children[0]".',
              );

              const isInPath = Number(indexInDirectoryName[1]) === idx;

              const newPropertyKey =
                directoriesBefore.length === 0
                  ? `${propertyKeyBeforeRoot}.${propertyKeyFromRoot[0]}.children[${idx}]`
                  : `${propertyKeyBeforeRoot}.${propertyKeyFromRoot[0]}.${directoriesBefore}.children[${idx}]`;

              return (
                <Tr key={id}>
                  <HStack spacing="4px">
                    <Button
                      key={id}
                      variant={
                        fullPropertyKey === newPropertyKey ? 'solid' : 'outline'
                      }
                      colorScheme={isInPath ? 'blue' : 'gray'}
                      size="sm"
                      width="full"
                      justifyContent="flex-start"
                      onClick={() => {
                        setSelectedModifierNode({
                          propertyKey: newPropertyKey,
                          depth: directoryIndex + 1,
                        });
                      }}
                    >
                      {node.name}
                    </Button>
                  </HStack>
                </Tr>
              );
            })}
          {isEditableIndex &&
            childrenNodeState.map((node, idx) => {
              const id = getIterationKey(node);
              return (
                <TrDraggableHandle
                  acceptedType="node"
                  id={id}
                  key={id}
                  index={idx}
                  moveRow={moveRow}
                  isDragDisabled={isOrderLocked}
                  draggableIconProps={{
                    padding: '2px',
                    borderBottomWidth: '0px',
                  }}
                >
                  <HStack spacing="4px">
                    <Button
                      key={id}
                      variant="outline"
                      colorScheme="gray"
                      size="sm"
                      width="full"
                      justifyContent="flex-start"
                      onClick={() => {
                        setSelectedModifierNode({
                          propertyKey: `${childrenPropertyKey}[${idx}]`,
                          depth: depth + 1,
                        });
                      }}
                    >
                      {node.name}
                    </Button>
                    {wipConfig?.isMutateAllowed(fullPropertyKey) &&
                      isOrderLocked && (
                        <DeleteIconButton
                          ariaLabel="delete"
                          label="Delete"
                          onClick={() => {
                            setNodeIdxToDelete(idx);
                          }}
                          variant="ghost"
                        />
                      )}
                  </HStack>
                </TrDraggableHandle>
              );
            })}
          {nodeIdxToDelete !== undefined &&
            wipConfig?.isMutateAllowed(fullPropertyKey) && (
              <DeleteModal
                isOpen={nodeIdxToDelete !== undefined}
                title="Delete Modifier Node"
                message="Are you sure you want to delete this modifier node?"
                onCancel={() => {
                  setNodeIdxToDelete(undefined);
                }}
                onSave={() => {
                  deleteModifierNode(wipConfig, nodeIdxToDelete);
                  setNodeIdxToDelete(undefined);
                }}
              />
            )}
        </Tbody>
      </Table>
    </>
  );
};

type ReceivableAdvanceRateModifierTreeProps = {
  advanceRateTreeConfig: CreditAgreement.Config.ReceivableAdvanceRate.Tree;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  fullPropertyKey: string; // CA path to current node (e.g. ...modifiers[0].children[1].children[0].children[2])
  depth: number; // modifiers[0] has depth 0
  baseFields: CreditAgreement.DataTapeField.BaseConfig[];
  intermediateCalcs: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[];
  concentrationLimitConfigs: CreditAgreement.Config.ConcentrationLimit.SourceConfig[];
  returnToModifierTreesTableView: () => void;
};
export const ReceivableAdvanceRateModifierTree = ({
  advanceRateTreeConfig,
  wipConfig,
  fullPropertyKey,
  depth,
  returnToModifierTreesTableView,
  baseFields,
  intermediateCalcs,
  concentrationLimitConfigs,
}: ReceivableAdvanceRateModifierTreeProps) => {
  // e.g. ["modifiers[0]", "children[1]", "children[0]"]
  const propertyKeyFromRoot: string[] = fullPropertyKey
    .split('.')
    .slice(-1 * (depth + 1));

  // e.g. "...receivableAdvanceRate._config_.receivableAdvanceRateConfig"
  const propertyKeyBeforeRoot: string = fullPropertyKey
    .split('.')
    .slice(0, -1 * (depth + 1))
    .join('.');

  // get directory list to iterate over (e.g. ["children[1]", "children[0]", "children"])
  const directories = [...propertyKeyFromRoot.slice(1), 'children'];

  // get current node
  const modifierNode: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateModifierNode = _.get(
    advanceRateTreeConfig,
    propertyKeyFromRoot.join('.'),
  );

  // modifier tree name
  const modifierTreeRootName: string = _.get(
    advanceRateTreeConfig,
    propertyKeyFromRoot[0],
  ).name;

  const isRootOfTree = depth === 0;

  // needed for select navigation between different modifier trees
  const modifierTreeRootIndexMatch = propertyKeyFromRoot[0].match(
    /modifiers\[(\d+)\]/,
  );
  finleyAssert(
    modifierTreeRootIndexMatch && modifierTreeRootIndexMatch.length === 2, // [match, group]
    `Missing index on modifier tree: ${propertyKeyFromRoot[0]}`,
  );

  const navigateModifierTreeOptions = advanceRateTreeConfig.modifiers.map(
    (m, idx) => {
      return {
        label: m.name,
        value: idx.toString(),
      };
    },
  );

  // create breadcrumbs name list
  const nodeNamesBreadcrumbs: string[] = [];
  propertyKeyFromRoot.forEach((_name, index) => {
    const node = _.get(
      advanceRateTreeConfig,
      propertyKeyFromRoot.slice(0, index + 1).join('.'),
    );
    nodeNamesBreadcrumbs.push(node.name);
  });

  const [canEditNode, setCanEditNode] = useState<boolean>(false);

  const [selectedModifierNode, setSelectedModifierNode] = useState<{
    propertyKey: string;
    depth: number;
  }>();

  if (selectedModifierNode) {
    const newPropertyKeyFromRoot: string[] = selectedModifierNode.propertyKey
      .split('.')
      .slice(-1 * (selectedModifierNode.depth + 1));

    const newModifierNode: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateNode = _.get(
      advanceRateTreeConfig,
      newPropertyKeyFromRoot.join('.'),
    );

    if (newModifierNode) {
      return (
        <ReceivableAdvanceRateModifierTree
          advanceRateTreeConfig={advanceRateTreeConfig}
          fullPropertyKey={selectedModifierNode.propertyKey}
          depth={selectedModifierNode.depth}
          wipConfig={wipConfig}
          returnToModifierTreesTableView={returnToModifierTreesTableView}
          baseFields={baseFields}
          intermediateCalcs={intermediateCalcs}
          concentrationLimitConfigs={concentrationLimitConfigs}
        />
      );
    }
  }

  return (
    <>
      <HStack mb={4}>
        <Button
          fontSize="sm"
          onClick={() => {
            returnToModifierTreesTableView();
          }}
          variant="link"
        >
          Modifiers
        </Button>
        <Icon icon="angle-right" />
        <Text fontSize="sm">{modifierTreeRootName}</Text>
      </HStack>

      <Box width="full" mb={4}>
        <Select
          options={navigateModifierTreeOptions}
          value={modifierTreeRootIndexMatch[1]} // index of the current modifier tree root
          onChange={(val) => {
            setSelectedModifierNode({
              propertyKey: `${propertyKeyBeforeRoot}.modifiers[${val}]`,
              depth: 0,
            });
          }}
        />
      </Box>

      <Stack spacing="20px">
        {/* Below is file traversal UI, this will be breadcrumbs rather than a recursive component on borrower dash */}
        <HStack
          spacing="25px"
          alignItems="flex-start"
          width="full"
          overflowX="auto"
        >
          {directories.map((directoryName, directoryIndex) => {
            const directoriesBefore = directories
              .slice(0, directoryIndex)
              .join('.');
            const previousNode: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateModifierNode | null =
              directoryIndex > 0
                ? _.get(
                    advanceRateTreeConfig,
                    `${propertyKeyFromRoot[0]}.${directoriesBefore}`,
                  )
                : null;

            const childrenAtCurrentIndex: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateModifierNode[] = _.get(
              advanceRateTreeConfig,
              directoriesBefore.length === 0
                ? `${propertyKeyFromRoot[0]}.children`
                : `${propertyKeyFromRoot[0]}.${directoriesBefore}.children`,
            );
            const isEditableIndex = directoryIndex === directories.length - 1; // you can only edit the last level, which is modifiers[0].children;

            return (
              <Box
                key={uuidV4()}
                height="fit-content"
                minHeight="min-content"
                maxHeight="550px"
                width="20%"
                padding={4}
                borderWidth="1px"
                borderRadius="8px"
                overflowY="scroll"
                overflowX="clip"
                flexShrink={0}
              >
                {previousNode ? (
                  <Text fontSize="15px" fontWeight="bold">
                    {previousNode.name}
                  </Text>
                ) : (
                  <Button
                    colorScheme={isRootOfTree ? 'blue' : 'gray'}
                    variant={isRootOfTree ? 'solid' : 'outline'}
                    size="sm"
                    isDisabled={isRootOfTree}
                    _disabled={{ colorSchema: 'blue', cursor: 'inherit' }}
                    onClick={() => {
                      setSelectedModifierNode({
                        propertyKey: `${propertyKeyBeforeRoot}.${propertyKeyFromRoot[0]}`,
                        depth: 0,
                      });
                    }}
                    width="full"
                    justifyContent="flex-start"
                    mb={2}
                  >
                    {modifierTreeRootName}
                  </Button>
                )}

                <ModifierLevelTable
                  modifierNode={modifierNode}
                  wipConfig={wipConfig}
                  fullPropertyKey={fullPropertyKey}
                  depth={depth}
                  isEditableIndex={isEditableIndex}
                  childrenAtCurrentIndex={childrenAtCurrentIndex}
                  directoriesBefore={directoriesBefore}
                  directoryIndex={directoryIndex}
                  directoryName={directoryName}
                  propertyKeyBeforeRoot={propertyKeyBeforeRoot}
                  propertyKeyFromRoot={propertyKeyFromRoot}
                  setSelectedModifierNode={setSelectedModifierNode}
                />
              </Box>
            );
          })}
        </HStack>

        {/* Below is logic for editing/viewing current node */}

        <Box padding={6} borderWidth="1px" borderRadius="8px" width="full">
          <HStack>
            <Text mb={4} fontSize="18px" fontWeight="bold">
              {modifierNode.name}
            </Text>
            <Spacer />
            {wipConfig?.isMutateAllowed(fullPropertyKey) && !canEditNode && (
              <EditIconButton
                ariaLabel="edit default advance rate"
                label="Edit Default Advance Rate"
                onClick={() => {
                  setCanEditNode(true);
                }}
              />
            )}
          </HStack>

          <HStack mb={4}>
            {nodeNamesBreadcrumbs.map((name, idx) => {
              return (
                <>
                  <Box
                    bgColor="blue.500"
                    color="white"
                    borderRadius="8px"
                    py={1}
                    px={3}
                    fontSize="xs"
                    key={uuidV4()}
                  >
                    <Text>{name}</Text>
                  </Box>
                  {idx !== nodeNamesBreadcrumbs.length - 1 && (
                    <Icon
                      icon="angle-right"
                      color={
                        // !TODO @eldarshamukhamedov This breaks rules of hooks
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useColorModeValue('black', 'white')
                      }
                    />
                  )}
                </>
              );
            })}
          </HStack>

          {canEditNode && wipConfig?.isMutateAllowed(fullPropertyKey) ? (
            <Box width="full" justifyContent="end">
              <EditModifierNode
                onSave={(
                  props: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateModifierNode,
                ) => {
                  wipConfig.formProps.setFieldValue(fullPropertyKey, props);
                  setCanEditNode(false);
                }}
                onCancel={() => {
                  setCanEditNode(false);
                }}
                node={modifierNode}
                operationMode={wipConfig?.operationMode}
                baseFields={baseFields}
                intermediateCalcs={intermediateCalcs}
                concentrationLimitConfigs={concentrationLimitConfigs}
              />
            </Box>
          ) : (
            <>
              <HStack width="full" spacing="20px" alignItems="flex-start">
                <Box width="70%">
                  <Text mb={2} fontSize="15px" fontWeight="bold">
                    Condition
                  </Text>
                  <Box width="full" justifyContent="end" whiteSpace="pre-wrap">
                    <FormulaContainer>
                      {prettyPrintFormula(modifierNode.isMatch.formula)}
                    </FormulaContainer>
                  </Box>
                </Box>
                <Box width="30%">
                  <Text mb={2} fontSize="15px" fontWeight="bold">
                    Formula
                  </Text>
                  <Box width="full" justifyContent="end" whiteSpace="pre-wrap">
                    <FormulaContainer>
                      {prettyPrintFormula(modifierNode.modifierFormula.formula)}
                    </FormulaContainer>
                  </Box>
                </Box>
              </HStack>
            </>
          )}
        </Box>
      </Stack>
    </>
  );
};

type EditModifierNameProps = {
  modifier: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateModifierNode;
  onSubmit: (name: string) => void;
  onCancel: () => void;
};
const EditModifierName = ({
  modifier,
  onSubmit,
  onCancel,
}: EditModifierNameProps) => {
  return (
    <>
      <Formik
        initialValues={{
          ...modifier,
        }}
        onSubmit={(props) => {
          onSubmit(_.camelCase(props.name));
        }}
      >
        {(
          localFormProps: FormikProps<CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateModifierNode>,
        ) => {
          return (
            <Td colSpan={2}>
              <HStack>
                <TextInputFormField label="" name="name" />

                <Spacer />
                <Box width="15%" mt="4px">
                  <HStack>
                    <Button
                      display="block"
                      w="100%"
                      color={colors.aurora[400]}
                      border="2px"
                      borderColor={colors.aurora[400]}
                      backgroundColor="white"
                      onClick={() => {
                        onCancel();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      display="block"
                      w="100%"
                      color="white"
                      backgroundColor={colors.aurora[400]}
                      type="submit"
                      onClick={() => localFormProps.handleSubmit()}
                    >
                      Save
                    </Button>
                  </HStack>
                </Box>
              </HStack>
            </Td>
          );
        }}
      </Formik>
    </>
  );
};

type ReceivableAdvanceRateModifiersTableProps = {
  advanceRateTreeConfig: CreditAgreement.Config.ReceivableAdvanceRate.Tree;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  fullPropertyKey: string;
  baseFields: CreditAgreement.DataTapeField.BaseConfig[];
  intermediateCalcs: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[];
  concentrationLimitConfigs: CreditAgreement.Config.ConcentrationLimit.SourceConfig[];
};
export const ReceivableAdvanceRateModifiersTable = ({
  advanceRateTreeConfig,
  wipConfig,
  fullPropertyKey,
  baseFields,
  intermediateCalcs,
  concentrationLimitConfigs,
}: ReceivableAdvanceRateModifiersTableProps) => {
  const [selectedModifierTree, setSelectedModifierTree] = useState<{
    tree: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateModifierNode;
    index: number;
  }>();

  const [editableIndex, setEditableIndex] = useState<number>();

  const addModifierTree = (
    wc: CreditAgreementFullArtifactWipConfigType,
  ): void => {
    const modifierTreesCopy = _.cloneDeep(
      getIn(wc.formProps.values, fullPropertyKey),
    );

    const field = createModifierShell({
      isRoot: true,
      idx: modifierTreesCopy.length,
    });
    modifierTreesCopy.push(field);

    wc.formProps.setFieldValue(fullPropertyKey, modifierTreesCopy);
  };

  const [treeIdxToDelete, setTreeIdxToDelete] = useState<number>();

  const deleteModifierTree = (
    wc: CreditAgreementFullArtifactWipConfigType,
    idxToDelete: number,
  ): void => {
    const modifierTreesCopy = _.cloneDeep(
      getIn(wc.formProps.values, fullPropertyKey),
    );

    modifierTreesCopy.splice(idxToDelete, 1);
    wc.formProps.setFieldValue(fullPropertyKey, modifierTreesCopy);
  };

  if (selectedModifierTree) {
    return (
      <ReceivableAdvanceRateModifierTree
        advanceRateTreeConfig={advanceRateTreeConfig}
        fullPropertyKey={`${fullPropertyKey}[${selectedModifierTree.index}]`}
        depth={0}
        wipConfig={wipConfig}
        returnToModifierTreesTableView={() => {
          setSelectedModifierTree(undefined);
        }}
        baseFields={baseFields}
        intermediateCalcs={intermediateCalcs}
        concentrationLimitConfigs={concentrationLimitConfigs}
      />
    );
  }

  return (
    <Stack spacing="20px">
      <HStack>
        <Spacer />
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <AddButton
            title="Add Modifier"
            onClick={() => {
              addModifierTree(wipConfig);
            }}
          />
        )}
      </HStack>
      <TableContainer>
        <Table variant="simple" borderWidth="1px">
          <Thead>
            <Tr>
              <Th>Modifiers</Th>
            </Tr>
          </Thead>
          <Tbody>
            {advanceRateTreeConfig.modifiers.length === 0 && (
              <Tr>
                <Td>
                  <Text>No modifiers are configured.</Text>
                </Td>
              </Tr>
            )}
            {advanceRateTreeConfig.modifiers.map((m, idx) => {
              return (
                <Tr key={getIterationKey(m)}>
                  {wipConfig?.isMutateAllowed(fullPropertyKey) &&
                  editableIndex !== undefined &&
                  editableIndex === idx ? (
                    <EditModifierName
                      modifier={m}
                      onSubmit={(newName: string) => {
                        wipConfig.formProps.setFieldValue(
                          `${fullPropertyKey}[${editableIndex}].name`,
                          newName,
                        );
                        setEditableIndex(undefined);
                      }}
                      onCancel={() => {
                        setEditableIndex(undefined);
                      }}
                    />
                  ) : (
                    <Td>
                      <HStack>
                        <Button
                          variant="link"
                          colorScheme="blue"
                          color="blue.500"
                          onClick={() => {
                            setSelectedModifierTree({ tree: m, index: idx });
                          }}
                        >
                          {m.name}
                        </Button>
                        <Spacer />
                        {wipConfig?.isMutateAllowed(fullPropertyKey) &&
                          editableIndex === undefined && (
                            <HStack>
                              <Spacer />
                              <EditIconButton
                                ariaLabel="edit modifier name"
                                label="Edit Name"
                                onClick={() => {
                                  setEditableIndex(idx);
                                }}
                              />
                              <DeleteIconButton
                                ariaLabel="delete entire modifier"
                                label="Delete Modifier"
                                onClick={() => {
                                  setTreeIdxToDelete(idx);
                                }}
                                variant="ghost"
                              />
                            </HStack>
                          )}
                      </HStack>
                    </Td>
                  )}
                </Tr>
              );
            })}
            {treeIdxToDelete !== undefined &&
              wipConfig?.isMutateAllowed(fullPropertyKey) && (
                <DeleteModal
                  isOpen={treeIdxToDelete !== undefined}
                  title="Delete Modifier"
                  message="Are you sure you want to delete this modifier?"
                  onCancel={() => {
                    setTreeIdxToDelete(undefined);
                  }}
                  onSave={() => {
                    deleteModifierTree(wipConfig, treeIdxToDelete);
                    setTreeIdxToDelete(undefined);
                  }}
                />
              )}
          </Tbody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

/**
 * ============= ADVANCE RATE NODE CONFIGURATION =============
 */

type AdvanceRateNodeParams =
  | {
      depth: number;
      idx: number;
      isRoot?: never;
    }
  | {
      depth?: never;
      idx?: never;
      isRoot: boolean;
    };

export const createAdvanceRateNodeShell = (
  params: AdvanceRateNodeParams,
): CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateNode => ({
  name: params.isRoot ? 'root' : `[ name:${params.depth}.${params.idx} ]`,
  isMatch: {
    formula: 'TRUE',
    engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
  },
  advanceRate: {
    formula: '0',
    engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
  },
  children: [],
  modifierNames: [],
});

type AttachModifierModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (value: string) => void;
  modifierNames: string[];
};
const AttachModifierModal = ({
  isOpen,
  onCancel,
  onSave,
  modifierNames,
}: AttachModifierModalProps) => {
  const buttonColorScheme = useColorModeValue('aurora', 'gray');
  const [selectedModifier, setSelectedModifier] = useState<string>();
  return (
    <Modal isOpen={isOpen} onClose={() => onCancel()}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Attach Modifier</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {modifierNames.length > 0 ? (
            <>
              <Box mb={2}>
                <Select
                  placeholderLabel="Select a modifier"
                  value={selectedModifier}
                  options={formatSelectOptions(modifierNames)}
                  onChange={(value) => {
                    setSelectedModifier(value);
                  }}
                />
              </Box>
              <Box py="12px" alignSelf="flex-end">
                <HStack spacing="25px">
                  <Spacer />
                  <Button
                    variant="outline"
                    colorScheme={buttonColorScheme}
                    onClick={() => onCancel()}
                    px="20px"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="solid"
                    colorScheme={buttonColorScheme}
                    isDisabled={!selectedModifier}
                    onClick={() => {
                      if (selectedModifier) {
                        onSave(selectedModifier);
                      }
                    }}
                    px="20px"
                  >
                    Attach
                  </Button>
                </HStack>
              </Box>
            </>
          ) : (
            <Text mb={4}>Set up modifier to attach them here.</Text>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type DetachModifierModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSave: () => void;
};
const DetachModifierModal = ({
  isOpen,
  onCancel,
  onSave,
}: DetachModifierModalProps) => {
  const cancelRef = React.useRef(null);
  const buttonColorScheme = useColorModeValue('aurora', 'gray');
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCancel}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Detach Modifier
          </AlertDialogHeader>

          <AlertDialogBody>
            <>
              <Text mb={2}>
                Are you sure you&apos;d like to detach this modifier?
              </Text>
              <Box py="12px" alignSelf="flex-end">
                <HStack spacing="25px">
                  <Spacer />
                  <Button
                    variant="outline"
                    colorScheme={buttonColorScheme}
                    onClick={() => onCancel()}
                    px="20px"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="solid"
                    colorScheme={buttonColorScheme}
                    onClick={() => {
                      onSave();
                    }}
                    px="20px"
                  >
                    Detach
                  </Button>
                </HStack>
              </Box>
            </>
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

type EditDefaultAdvanceRateProps = {
  formula: string;
  onSave: Function;
  onCancel: Function;
  operationMode?: CreditAgreement.OperationMode;
  baseFields: CreditAgreement.DataTapeField.BaseConfig[];
  intermediateCalcs: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[];
  concentrationLimitConfigs: CreditAgreement.Config.ConcentrationLimit.SourceConfig[];
};
const EditDefaultAdvanceRate = ({
  formula,
  onSave,
  onCancel,
  operationMode,
  baseFields,
  intermediateCalcs,
  concentrationLimitConfigs,
}: EditDefaultAdvanceRateProps) => {
  const [errors, setErrors] = useState<string[]>([]);
  const isFormulaValid = useCallback(
    (formulaToValidate: string) => {
      const formulaErrors = checkFormula({
        formula: formulaToValidate,
        baseFields,
        intermediateCalcs,
        concentrationLimitConfigs,
      });

      setErrors(formulaErrors);
      return formulaErrors.length === 0;
    },
    [baseFields, intermediateCalcs, concentrationLimitConfigs],
  );
  return (
    <>
      <Formik
        initialValues={{ formula }}
        onSubmit={(props) => {
          if (isFormulaValid(props.formula)) {
            onSave(props);
          }
        }}
      >
        {(localFormProps: FormikProps<{ formula: string }>) => {
          return (
            <Stack>
              <HStack width="full">
                <Box
                  width="full"
                  justifyContent="end"
                  border="1px"
                  borderColor={colors.gray[200]}
                  borderRadius="8px"
                  p="8px"
                >
                  <FormulaEditor
                    value={formula}
                    onChange={(value) => {
                      localFormProps.setFieldValue('formula', value);
                    }}
                    operationMode={operationMode}
                    language="formula-receivableAdvanceRate"
                  />
                </Box>

                <Spacer />
                <Box width="10%">
                  <Button
                    display="block"
                    w="100%"
                    mb="16px"
                    color="white"
                    backgroundColor={colors.aurora[400]}
                    type="submit"
                    onClick={() => localFormProps.handleSubmit()}
                  >
                    Save
                  </Button>
                  <Button
                    display="block"
                    w="100%"
                    color={colors.aurora[400]}
                    border="2px"
                    borderRadius="8px"
                    borderColor={colors.aurora[400]}
                    backgroundColor="white"
                    onClick={() => onCancel()}
                  >
                    Cancel
                  </Button>
                </Box>
              </HStack>
              {errors.length > 0 && (
                <Box
                  width="full"
                  padding="8px"
                  borderRadius="8px"
                  bgColor="red.100"
                >
                  {JSON.stringify(errors)}
                </Box>
              )}
            </Stack>
          );
        }}
      </Formik>
    </>
  );
};

type EditAdvanceRateNodeProps = {
  onSave: Function;
  onCancel: Function;
  node: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateNode;
  operationMode?: CreditAgreement.OperationMode;
  baseFields: CreditAgreement.DataTapeField.BaseConfig[];
  intermediateCalcs: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[];
  concentrationLimitConfigs: CreditAgreement.Config.ConcentrationLimit.SourceConfig[];
};
const EditAdvanceRateNode = ({
  onSave,
  onCancel,
  node,
  operationMode,
  baseFields,
  intermediateCalcs,
  concentrationLimitConfigs,
}: EditAdvanceRateNodeProps) => {
  const [conditionErrors, setConditionErrors] = useState<string[]>([]);
  const [advanceRateErrors, setAdvanceRateErrors] = useState<string[]>([]);

  const isFormulaValid = useCallback(
    ({
      conditionToValidate,
      advanceRateToValidate,
    }: {
      conditionToValidate: string;
      advanceRateToValidate: string;
    }) => {
      const conditionFormulaErrors = checkFormula({
        formula: conditionToValidate,
        baseFields,
        intermediateCalcs,
        concentrationLimitConfigs,
      });

      const advanceRateFormulaErrors = checkFormula({
        formula: advanceRateToValidate,
        baseFields,
        intermediateCalcs,
        concentrationLimitConfigs,
      });

      setConditionErrors(conditionFormulaErrors);
      setAdvanceRateErrors(advanceRateFormulaErrors);

      return (
        conditionFormulaErrors.length === 0 &&
        advanceRateFormulaErrors.length === 0
      );
    },
    [baseFields, intermediateCalcs, concentrationLimitConfigs],
  );

  return (
    <>
      <Formik
        initialValues={{ ...node }}
        onSubmit={(props) => {
          if (
            isFormulaValid({
              conditionToValidate: props.isMatch.formula,
              advanceRateToValidate: props.advanceRate.formula,
            })
          ) {
            onSave({ ...props, name: _.camelCase(props.name) });
          }
        }}
      >
        {(
          localFormProps: FormikProps<CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateNode>,
        ) => (
          <Stack spacing="20px">
            <TextInputFormField label="Advance Rate Name" name="name" />

            <HStack width="full" spacing="20px">
              <Box width="60%">
                <Text mb={2} fontSize="15px" fontWeight="bold">
                  Condition
                </Text>
                <Box
                  border="1px"
                  borderColor={colors.gray[200]}
                  borderRadius="8px"
                  p="8px"
                >
                  <FormulaEditor
                    value={localFormProps.values.isMatch.formula}
                    onChange={(value) => {
                      localFormProps.setFieldValue('isMatch.formula', value);
                    }}
                    operationMode={operationMode}
                    language="formula-receivableAdvanceRate"
                  />
                </Box>
              </Box>
              <Box width="30%">
                <Text mb={2} fontSize="15px" fontWeight="bold">
                  Advance Rate
                </Text>
                <Box
                  border="1px"
                  borderColor={colors.gray[200]}
                  borderRadius="8px"
                  p="8px"
                >
                  <FormulaEditor
                    value={localFormProps.values.advanceRate.formula}
                    onChange={(value) => {
                      localFormProps.setFieldValue(
                        'advanceRate.formula',
                        value,
                      );
                    }}
                    operationMode={operationMode}
                    language="formula-receivableAdvanceRate"
                  />
                </Box>
              </Box>

              <Box width="10%">
                <Button
                  display="block"
                  w="100%"
                  mt="28px"
                  mb="16px"
                  color="white"
                  backgroundColor={colors.aurora[400]}
                  type="submit"
                  onClick={() => localFormProps.handleSubmit()}
                >
                  Save
                </Button>
                <Button
                  display="block"
                  w="100%"
                  color={colors.aurora[400]}
                  border="2px"
                  borderRadius="8px"
                  borderColor={colors.aurora[400]}
                  backgroundColor="white"
                  onClick={() => onCancel()}
                >
                  Cancel
                </Button>
              </Box>
            </HStack>
            {conditionErrors.length > 0 && (
              <Box
                width="full"
                padding="8px"
                borderRadius="8px"
                bgColor="red.100"
              >
                {`Condition Errors: ${JSON.stringify(conditionErrors)}`}
              </Box>
            )}
            {advanceRateErrors.length > 0 && (
              <Box
                width="full"
                padding="8px"
                borderRadius="8px"
                bgColor="red.100"
              >
                {`Advance Rate Errors: ${JSON.stringify(advanceRateErrors)}`}
              </Box>
            )}
          </Stack>
        )}
      </Formik>
    </>
  );
};

type AdvanceRateLevelTableProps = {
  advanceRateNode: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateNode;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  fullPropertyKey: string;
  depth: number;
  isEditableIndex: boolean;
  childrenAtCurrentIndex: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateNode[];
  directoriesBefore: string;
  directoryName: string;
  directoryIndex: number;
  propertyKeyBeforeRoot: string;
  setSelectedAdvanceRate: React.Dispatch<
    React.SetStateAction<{ propertyKey: string; depth: number } | undefined>
  >;
};
const AdvanceRateLevelTable = ({
  advanceRateNode,
  wipConfig,
  fullPropertyKey,
  depth,
  isEditableIndex,
  childrenAtCurrentIndex,
  directoriesBefore,
  directoryName,
  directoryIndex,
  propertyKeyBeforeRoot,
  setSelectedAdvanceRate,
}: AdvanceRateLevelTableProps) => {
  const childrenPropertyKey = `${fullPropertyKey}.children`;

  const {
    useRowState,
    useOrderLockedState,
    moveRow,
    ui: { LockOrderButton, RestoreOrderButton, SaveOrderCallout },
  } = useDraggableRow<CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateNode>(
    { comparisonKey: 'name' },
  );
  const [isOrderLocked] = useOrderLockedState();
  const [childrenNodeState, setChildrenNodeState] = useRowState(
    advanceRateNode.children,
  );

  const onSaveOrder = useCallback((): void => {
    wipConfig?.formProps.setFieldValue(
      `${childrenPropertyKey}`,
      childrenNodeState,
    );
  }, [childrenPropertyKey, childrenNodeState, wipConfig]);

  const addAdvanceRate = (
    wc: CreditAgreementFullArtifactWipConfigType,
  ): void => {
    const children = _.cloneDeep(
      getIn(wc.formProps.values, childrenPropertyKey),
    );

    const field = createAdvanceRateNodeShell({
      depth,
      idx: children.length,
    });
    children.push(field);

    wc.formProps.setFieldValue(childrenPropertyKey, children);
    setChildrenNodeState(children);
  };

  const [nodeIdxToDelete, setNodeIdxToDelete] = useState<number>();

  const deleteAdvanceRate = (
    wc: CreditAgreementFullArtifactWipConfigType,
    idxToDelete: number,
  ): void => {
    const childrenCopy = _.cloneDeep(
      getIn(wc.formProps.values, childrenPropertyKey),
    );

    childrenCopy.splice(idxToDelete, 1);
    wc.formProps.setFieldValue(childrenPropertyKey, childrenCopy);
    setChildrenNodeState(childrenCopy);
  };

  return (
    <>
      {wipConfig?.isMutateAllowed(fullPropertyKey) && <SaveOrderCallout />}

      <HStack mb={2}>
        {isEditableIndex && wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <LockOrderButton onSaveOrder={onSaveOrder} />
            {!isOrderLocked && <RestoreOrderButton />}
          </>
        )}
        <Spacer />
        {isEditableIndex &&
          wipConfig?.isMutateAllowed(fullPropertyKey) &&
          isOrderLocked && (
            <AddButton
              title="Add Advance Rate"
              onClick={() => {
                addAdvanceRate(wipConfig);
              }}
            />
          )}
      </HStack>

      <Table
        style={{
          borderCollapse: 'separate',
          borderSpacing: '0 16px',
        }}
      >
        <Tbody>
          {!isEditableIndex &&
            childrenAtCurrentIndex.map((node, idx) => {
              const id = getIterationKey(node);

              const indexInDirectoryName = directoryName.match(
                /children\[(\d+)\]/,
              );
              finleyAssert(
                indexInDirectoryName && indexInDirectoryName.length === 2,
                'Every level but the current advanceRateNode.children should follow this pattern: "children[0]".',
              );

              const isInPath = Number(indexInDirectoryName[1]) === idx;

              const newPropertyKey =
                directoriesBefore.length === 0
                  ? `${propertyKeyBeforeRoot}.root.children[${idx}]`
                  : `${propertyKeyBeforeRoot}.root.${directoriesBefore}.children[${idx}]`;

              return (
                <Tr key={id}>
                  <HStack spacing="4px">
                    <Button
                      key={id}
                      variant={
                        newPropertyKey === fullPropertyKey ? 'solid' : 'outline'
                      }
                      colorScheme={isInPath ? 'blue' : 'gray'}
                      size="sm"
                      width="full"
                      justifyContent="flex-start"
                      onClick={() => {
                        setSelectedAdvanceRate({
                          propertyKey: newPropertyKey,
                          depth: directoryIndex + 1,
                        });
                      }}
                    >
                      {node.modifierNames.length > 0 ? (
                        <HStack>
                          <Icon icon="arrow-turn-down-right" />
                          <Text>{node.name}</Text>
                        </HStack>
                      ) : (
                        <Text>{node.name}</Text>
                      )}
                    </Button>
                  </HStack>
                </Tr>
              );
            })}
          {isEditableIndex &&
            childrenNodeState.map((node, idx) => {
              const id = getIterationKey(node);
              return (
                <TrDraggableHandle
                  acceptedType="node"
                  id={id}
                  key={id}
                  index={idx}
                  moveRow={moveRow}
                  isDragDisabled={isOrderLocked}
                  draggableIconProps={{
                    padding: '2px',
                    borderBottomWidth: '0px',
                  }}
                >
                  <HStack spacing="4px">
                    <Button
                      key={id}
                      variant="outline"
                      colorScheme="gray"
                      size="sm"
                      width="full"
                      justifyContent="flex-start"
                      onClick={() => {
                        setSelectedAdvanceRate({
                          propertyKey: `${childrenPropertyKey}[${idx}]`,
                          depth: depth + 1,
                        });
                      }}
                    >
                      {node.modifierNames.length > 0 ? (
                        <HStack>
                          <Icon icon="arrow-turn-down-right" />
                          <Text>{node.name}</Text>
                        </HStack>
                      ) : (
                        <Text>{node.name}</Text>
                      )}
                    </Button>
                    {wipConfig?.isMutateAllowed(fullPropertyKey) &&
                      isOrderLocked && (
                        <DeleteIconButton
                          ariaLabel="delete"
                          label="Delete"
                          onClick={() => {
                            setNodeIdxToDelete(idx);
                          }}
                          variant="ghost"
                        />
                      )}
                  </HStack>
                </TrDraggableHandle>
              );
            })}
          {nodeIdxToDelete !== undefined &&
            wipConfig?.isMutateAllowed(fullPropertyKey) && (
              <DeleteModal
                isOpen={nodeIdxToDelete !== undefined}
                title="Delete Advance Rate"
                message="Are you sure you want to delete this advance rate?"
                onCancel={() => {
                  setNodeIdxToDelete(undefined);
                }}
                onSave={() => {
                  deleteAdvanceRate(wipConfig, nodeIdxToDelete);
                  setNodeIdxToDelete(undefined);
                }}
              />
            )}
        </Tbody>
      </Table>
    </>
  );
};

type ReceivableAdvanceRateTreeNodesProps = {
  advanceRateTreeConfig: CreditAgreement.Config.ReceivableAdvanceRate.Tree;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  modifierNames: string[];
  fullPropertyKey: string; // CA path to current node (e.g. ...root.children[1].children[0].children[2])
  depth: number; // root has depth 0
  baseFields: CreditAgreement.DataTapeField.BaseConfig[];
  intermediateCalcs: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[];
  concentrationLimitConfigs: CreditAgreement.Config.ConcentrationLimit.SourceConfig[];
};
export const ReceivableAdvanceRateTreeNodes = ({
  advanceRateTreeConfig,
  wipConfig,
  fullPropertyKey,
  modifierNames,
  depth,
  baseFields,
  intermediateCalcs,
  concentrationLimitConfigs,
}: ReceivableAdvanceRateTreeNodesProps) => {
  // e.g. ["root", "children[1]", "children[0]"]
  const propertyKeyFromRoot: string[] = fullPropertyKey
    .split('.')
    .slice(-1 * (depth + 1));

  finleyAssert(
    propertyKeyFromRoot.length > 0 && propertyKeyFromRoot[0] === 'root',
    `Receivable Advance Rate Tree [ propertyKeyFromRoot ] should start with "root". Invalid: ${propertyKeyFromRoot}`,
  );

  // e.g. "...receivableAdvanceRate._config_.receivableAdvanceRateConfig"
  const propertyKeyBeforeRoot: string = fullPropertyKey
    .split('.')
    .slice(0, -1 * (depth + 1))
    .join('.');

  // get directory list to iterate over (e.g. ["children[1]", "children[0]", "children"])
  const directories = [...propertyKeyFromRoot.slice(1), 'children'];

  // get current node
  const advanceRateNode: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateNode = _.get(
    advanceRateTreeConfig,
    propertyKeyFromRoot.join('.'),
  );

  // create breadcrumbs name list
  const nodeNamesBreadcrumbs: {
    name: string;
    modifierNames: string[];
  }[] = [];
  propertyKeyFromRoot.forEach((name, index) => {
    const node = _.get(
      advanceRateTreeConfig,
      propertyKeyFromRoot.slice(0, index + 1).join('.'),
    );

    if (name === 'root') {
      nodeNamesBreadcrumbs.push({
        name: 'default',
        modifierNames: node.modifierNames,
      });
    } else {
      nodeNamesBreadcrumbs.push({
        name: node.name,
        modifierNames: node.modifierNames,
      });
    }
  });

  const isRoot = depth === 0;

  const [canEditNode, setCanEditNode] = useState<boolean>(false);
  const [canAttachModifierNames, setCanAttachModifierNames] = useState<boolean>(
    false,
  );
  const [
    modifierNamesIndexToDetach,
    setModifierNamesIndexToDetach,
  ] = useState<number>();

  // gets set from the `Advance Rate Group` dropdown or `Navigate Groupings` buttons
  const [selectedAdvanceRate, setSelectedAdvanceRate] = useState<{
    propertyKey: string;
    depth: number;
  }>();

  if (selectedAdvanceRate) {
    const newPropertyKeyFromRoot: string[] = selectedAdvanceRate.propertyKey
      .split('.')
      .slice(-1 * (selectedAdvanceRate.depth + 1));

    const newAdvanceRateNode: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateNode = _.get(
      advanceRateTreeConfig,
      newPropertyKeyFromRoot.join('.'),
    );

    if (newAdvanceRateNode) {
      return (
        <ReceivableAdvanceRateTreeNodes
          advanceRateTreeConfig={advanceRateTreeConfig}
          fullPropertyKey={selectedAdvanceRate.propertyKey}
          depth={selectedAdvanceRate.depth}
          wipConfig={wipConfig}
          modifierNames={modifierNames}
          baseFields={baseFields}
          intermediateCalcs={intermediateCalcs}
          concentrationLimitConfigs={concentrationLimitConfigs}
        />
      );
    }
  }

  return (
    <>
      <Text fontSize="18px" fontWeight="bold" mb={4}>
        Advance Rate Tree
      </Text>

      <Stack spacing="20px">
        {/* Below is file traversal UI, this will be breadcrumbs rather than a recursive component on borrower dash */}
        <HStack
          spacing="25px"
          alignItems="flex-start"
          width="full"
          overflowX="auto"
        >
          {directories.map((directoryName, directoryIndex) => {
            const directoriesBefore = directories
              .slice(0, directoryIndex)
              .join('.');
            const previousNode: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateNode | null =
              directoryIndex > 0
                ? _.get(advanceRateTreeConfig, `root.${directoriesBefore}`)
                : null;

            const childrenAtCurrentIndex: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateNode[] = _.get(
              advanceRateTreeConfig,
              directoriesBefore.length === 0
                ? `root.children`
                : `root.${directoriesBefore}.children`,
            );
            const isEditableIndex = directoryIndex === directories.length - 1; // you can only edit the last level, which is advanceRateNode.children;

            return (
              <Box
                key={uuidV4()}
                height="fit-content"
                minHeight="min-content"
                maxHeight="550px"
                width="20%"
                padding={4}
                borderWidth="1px"
                borderRadius="8px"
                overflowY="scroll"
                overflowX="clip"
                flexShrink={0}
              >
                {previousNode ? (
                  <Text fontSize="15px" fontWeight="bold">
                    {previousNode.name}
                  </Text>
                ) : (
                  <Button
                    colorScheme={isRoot ? 'blue' : 'gray'}
                    variant={isRoot ? 'solid' : 'outline'}
                    size="sm"
                    isDisabled={isRoot}
                    _disabled={{ colorSchema: 'blue', cursor: 'inherit' }}
                    onClick={() => {
                      setSelectedAdvanceRate({
                        propertyKey: `${propertyKeyBeforeRoot}.root`,
                        depth: 0,
                      });
                    }}
                    width="full"
                    justifyContent="flex-start"
                    mb={2}
                  >
                    {advanceRateTreeConfig.root.modifierNames.length > 0 ? (
                      <HStack>
                        <Icon icon="arrow-turn-down-right" />
                        <Text>Default</Text>
                      </HStack>
                    ) : (
                      <Text>Default</Text>
                    )}
                  </Button>
                )}

                <AdvanceRateLevelTable
                  advanceRateNode={advanceRateNode}
                  wipConfig={wipConfig}
                  fullPropertyKey={fullPropertyKey}
                  depth={depth}
                  isEditableIndex={isEditableIndex}
                  childrenAtCurrentIndex={childrenAtCurrentIndex}
                  directoriesBefore={directoriesBefore}
                  directoryIndex={directoryIndex}
                  directoryName={directoryName}
                  propertyKeyBeforeRoot={propertyKeyBeforeRoot}
                  setSelectedAdvanceRate={setSelectedAdvanceRate}
                />
              </Box>
            );
          })}
        </HStack>

        {/* Attach Modifier Modal */}
        {wipConfig?.isMutateAllowed(fullPropertyKey) && canAttachModifierNames && (
          <AttachModifierModal
            isOpen={canAttachModifierNames}
            onCancel={() => {
              setCanAttachModifierNames(false);
            }}
            onSave={(val: string) => {
              if (!advanceRateNode.modifierNames.includes(val)) {
                const newModifierNames = [
                  ...advanceRateNode.modifierNames,
                  val,
                ];
                wipConfig.formProps.setFieldValue(
                  `${fullPropertyKey}.modifierNames`,
                  newModifierNames,
                );
              }
              setCanAttachModifierNames(false);
            }}
            modifierNames={modifierNames.filter(
              (name) => !advanceRateNode.modifierNames.includes(name),
            )} // prevent from adding an already attached modifierName
          />
        )}

        {/* Detach Modifier Modal */}
        {wipConfig?.isMutateAllowed(fullPropertyKey) &&
          modifierNamesIndexToDetach !== undefined && (
            <DetachModifierModal
              isOpen={modifierNamesIndexToDetach !== undefined}
              onCancel={() => {
                setModifierNamesIndexToDetach(undefined);
              }}
              onSave={() => {
                const copyOfModifiers = [...advanceRateNode.modifierNames];
                copyOfModifiers.splice(modifierNamesIndexToDetach, 1);

                wipConfig.formProps.setFieldValue(
                  `${fullPropertyKey}.modifierNames`,
                  copyOfModifiers,
                );
                setModifierNamesIndexToDetach(undefined);
              }}
            />
          )}

        {/* Below is logic for editing/viewing current node */}

        {/* Show default advance rate for root node */}
        {isRoot && (
          <Box padding={6} borderWidth="1px" borderRadius="8px" width="full">
            <HStack>
              <Text mb={4} fontSize="18px" fontWeight="bold">
                Default
              </Text>
              <Spacer />
              {wipConfig?.isMutateAllowed(fullPropertyKey) && !canEditNode && (
                <>
                  <PlusIconButton
                    ariaLabel="attach modifier"
                    label="Attach modifier"
                    onClick={() => {
                      setCanAttachModifierNames(true);
                    }}
                  />
                  <EditIconButton
                    ariaLabel="edit default advance rate"
                    label="Edit Default Advance Rate"
                    onClick={() => {
                      setCanEditNode(true);
                    }}
                  />
                </>
              )}
            </HStack>
            {canEditNode && wipConfig?.isMutateAllowed(fullPropertyKey) ? (
              <Box width="full" justifyContent="end">
                <EditDefaultAdvanceRate
                  formula={advanceRateNode.advanceRate.formula}
                  onSave={(props: { formula: string }) => {
                    wipConfig.formProps.setFieldValue(
                      `${fullPropertyKey}.advanceRate.formula`,
                      props.formula,
                    );
                    setCanEditNode(false);
                  }}
                  onCancel={() => {
                    setCanEditNode(false);
                  }}
                  operationMode={wipConfig.operationMode}
                  baseFields={baseFields}
                  intermediateCalcs={intermediateCalcs}
                  concentrationLimitConfigs={concentrationLimitConfigs}
                />
              </Box>
            ) : (
              <>
                <Text mb={2} fontSize="15px" fontWeight="bold">
                  Advance Rate
                </Text>
                <Box
                  mb={2}
                  width="full"
                  justifyContent="end"
                  whiteSpace="pre-wrap"
                >
                  <FormulaContainer>
                    {prettyPrintFormula(advanceRateNode.advanceRate.formula)}
                  </FormulaContainer>
                </Box>
                {advanceRateNode.modifierNames.map((modName, idx) => {
                  return (
                    <HStack key={modName} fontSize="sm" fontWeight="bold">
                      <Icon icon="arrow-turn-down-right" color="blue.500" />
                      <Text color="blue.500">{modName}</Text>
                      <XIconButton
                        ariaLabel="detach modifier"
                        label="Detach modifier"
                        size="sm"
                        color="gray.500"
                        onClick={() => {
                          setModifierNamesIndexToDetach(idx);
                        }}
                      />
                    </HStack>
                  );
                })}
              </>
            )}
          </Box>
        )}

        {/* Show name, match condition, and advance rate formula for all other nodes */}
        {!isRoot && (
          <Box padding={6} borderWidth="1px" borderRadius="8px" width="full">
            <HStack>
              <Text mb={4} fontSize="18px" fontWeight="bold">
                {advanceRateNode.name}
              </Text>
              <Spacer />
              {wipConfig?.isMutateAllowed(fullPropertyKey) && !canEditNode && (
                <>
                  <PlusIconButton
                    ariaLabel="attach modifier"
                    label="Attach modifier"
                    onClick={() => {
                      setCanAttachModifierNames(true);
                    }}
                  />
                  <EditIconButton
                    ariaLabel="edit default advance rate"
                    label="Edit Default Advance Rate"
                    onClick={() => {
                      setCanEditNode(true);
                    }}
                  />
                </>
              )}
            </HStack>

            <HStack mb={4}>
              {nodeNamesBreadcrumbs.map((node, idx) => {
                const hasModifiers = node.modifierNames.length;
                return (
                  <>
                    <Box
                      bgColor="blue.500"
                      color="white"
                      borderRadius="8px"
                      py={1}
                      px={3}
                      fontSize="xs"
                      key={getIterationKey(node)}
                    >
                      {hasModifiers ? (
                        <Popover trigger="hover" placement="top">
                          <PopoverTrigger>
                            <HStack>
                              <Icon icon="arrow-turn-down-right" />
                              <Text>{node.name}</Text>
                            </HStack>
                          </PopoverTrigger>
                          <PopoverContent
                            bg={
                              // !TODO @eldarshamukhamedov This breaks rules of hooks
                              // eslint-disable-next-line react-hooks/rules-of-hooks
                              useColorModeValue('millet.50', 'gray.600')
                            }
                            color={
                              // !TODO @eldarshamukhamedov This breaks rules of hooks
                              // eslint-disable-next-line react-hooks/rules-of-hooks
                              useColorModeValue('black', 'white')
                            }
                            w={{ base: '100%', lg: 'max-content' }}
                          >
                            <PopoverBody>
                              <Stack spacing="8px">
                                {node.modifierNames.map((modifierName) => {
                                  return (
                                    <HStack key={modifierName}>
                                      <Icon icon="arrow-turn-down-right" />
                                      <Text>{modifierName}</Text>
                                    </HStack>
                                  );
                                })}
                              </Stack>
                            </PopoverBody>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <Text>{node.name}</Text>
                      )}
                    </Box>
                    {idx !== nodeNamesBreadcrumbs.length - 1 && (
                      <Icon
                        icon="angle-right"
                        color={
                          // !TODO @eldarshamukhamedov This breaks rules of hooks
                          // eslint-disable-next-line react-hooks/rules-of-hooks
                          useColorModeValue('black', 'white')
                        }
                      />
                    )}
                  </>
                );
              })}
            </HStack>

            {canEditNode && wipConfig?.isMutateAllowed(fullPropertyKey) ? (
              <Box width="full" justifyContent="end">
                <EditAdvanceRateNode
                  onSave={(
                    props: CreditAgreement.Config.ReceivableAdvanceRate.AdvanceRateNode,
                  ) => {
                    wipConfig.formProps.setFieldValue(fullPropertyKey, props);
                    setCanEditNode(false);
                  }}
                  onCancel={() => {
                    setCanEditNode(false);
                  }}
                  node={advanceRateNode}
                  operationMode={wipConfig?.operationMode}
                  baseFields={baseFields}
                  intermediateCalcs={intermediateCalcs}
                  concentrationLimitConfigs={concentrationLimitConfigs}
                />
              </Box>
            ) : (
              <>
                <HStack
                  mb={2}
                  width="full"
                  spacing="20px"
                  alignItems="flex-start"
                >
                  <Box width="70%">
                    <Text mb={2} fontSize="15px" fontWeight="bold">
                      Condition
                    </Text>
                    <Box
                      width="full"
                      justifyContent="end"
                      whiteSpace="pre-wrap"
                    >
                      <FormulaContainer>
                        {prettyPrintFormula(advanceRateNode.isMatch.formula)}
                      </FormulaContainer>
                    </Box>
                  </Box>
                  <Box width="30%">
                    <Text mb={2} fontSize="15px" fontWeight="bold">
                      Advance Rate
                    </Text>
                    <Box
                      width="full"
                      justifyContent="end"
                      whiteSpace="pre-wrap"
                    >
                      <FormulaContainer>
                        {prettyPrintFormula(
                          advanceRateNode.advanceRate.formula,
                        )}
                      </FormulaContainer>
                    </Box>
                  </Box>
                </HStack>
                {advanceRateNode.modifierNames.map((modName, idx) => {
                  return (
                    <HStack key={modName} fontSize="sm" fontWeight="bold">
                      <Icon icon="arrow-turn-down-right" color="blue.500" />
                      <Text color="blue.500">{modName}</Text>
                      <XIconButton
                        ariaLabel="detach modifier"
                        label="Detach modifier"
                        size="sm"
                        color="gray.500"
                        onClick={() => {
                          setModifierNamesIndexToDetach(idx);
                        }}
                      />
                    </HStack>
                  );
                })}
              </>
            )}
          </Box>
        )}
      </Stack>
    </>
  );
};
